<template>
  <el-dialog
    :model-value="modelValue"
    :title="isEdit ? '编辑交易记录' : '新建交易记录'"
    width="600px"
    @update:model-value="$emit('update:modelValue', $event)"
    @closed="handleClosed"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
      v-loading="loading"
    >
      <el-form-item label="交易时间" prop="transaction_date">
        <el-date-picker
          v-model="form.transaction_date"
          type="datetime"
          placeholder="选择交易时间"
          format="YYYY-MM-DD HH:mm"
          value-format="YYYY-MM-DD HH:mm:ss"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item label="交易类型" prop="transaction_type">
        <el-select v-model="form.transaction_type" placeholder="选择交易类型" @change="handleTypeChange">
          <el-option label="支出" value="支出" />
          <el-option label="收入" value="收入" />
          <el-option label="转账" value="转账" />
          <el-option label="借入" value="借入" />
          <el-option label="借出" value="借出" />
          <el-option label="收款" value="收款" />
          <el-option label="还款" value="还款" />
        </el-select>
      </el-form-item>

      <el-form-item label="金额" prop="amount">
        <el-input-number
          v-model="form.amount"
          :precision="2"
          :min="0.01"
          :max="9999999.99"
          placeholder="请输入金额"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item label="主账户" prop="primary_account_id">
        <AccountSelect v-model="form.primary_account_id" placeholder="选择主账户" style="width:100%" :includeHidden="true" />
      </el-form-item>

      <el-form-item
        v-if="showSecondaryAccount"
        label="次账户"
        prop="secondary_account_id"
      >
        <AccountSelect v-model="form.secondary_account_id" placeholder="选择次账户" style="width:100%" :includeHidden="true" />
      </el-form-item>

      <!-- 支出分类 -->
      <template v-if="form.transaction_type === '支出'">
        <el-form-item label="支出大类" prop="expense_major_category_id">
          <el-select 
            v-model="form.expense_major_category_id" 
            placeholder="选择支出大类"
            @change="handleExpenseMajorChange"
          >
            <el-option
              v-for="category in expenseMajorCategories"
              :key="category.id"
              :label="category.category_name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="支出小类">
          <el-select v-model="form.expense_minor_category_id" placeholder="选择支出小类">
            <el-option
              v-for="category in expenseMinorCategories"
              :key="category.id"
              :label="category.category_name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
      </template>

      <!-- 收入分类 -->
      <template v-if="form.transaction_type === '收入'">
        <el-form-item label="收入大类" prop="income_major_category_id">
          <el-select 
            v-model="form.income_major_category_id" 
            placeholder="选择收入大类"
            @change="handleIncomeMajorChange"
          >
            <el-option
              v-for="category in incomeMajorCategories"
              :key="category.id"
              :label="category.category_name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="收入小类">
          <el-select v-model="form.income_minor_category_id" placeholder="选择收入小类">
            <el-option
              v-for="category in incomeMinorCategories"
              :key="category.id"
              :label="category.category_name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
      </template>

      <el-form-item label="备注">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="3"
          placeholder="请输入备注信息"
        />
      </el-form-item>

      <el-form-item label="标签">
        <el-input v-model="form.tags" placeholder="请输入标签，多个标签用逗号分隔" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">取消</el-button>
      <el-button type="primary" @click="handleSave" :loading="loading">
        {{ isEdit ? '更新' : '创建' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import api from '../api'
import dayjs from 'dayjs'
import AccountSelect from './AccountSelect.vue'

export default {
  name: 'TransactionDialog',
  components: { AccountSelect },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    transaction: {
      type: Object,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'saved'],
  setup (props, { emit }) {
    const store = useStore()
    const formRef = ref()
    const loading = ref(false)
    
    const form = reactive({
      transaction_date: null,
      transaction_type: '支出',
      amount: null,
      primary_account_id: null,
      secondary_account_id: null,
      expense_major_category_id: null,
      expense_minor_category_id: null,
      income_major_category_id: null,
      income_minor_category_id: null,
      counterparty_name: '',
      description: '',
      tags: ''
    })

    const rules = {
      transaction_date: [
        { required: true, message: '请选择交易时间', trigger: 'change' }
      ],
      transaction_type: [
        { required: true, message: '请选择交易类型', trigger: 'change' }
      ],
      amount: [
        { required: true, message: '请输入金额', trigger: 'blur' }
      ],
      primary_account_id: [
        { required: true, message: '请选择主账户', trigger: 'change' }
      ]
    }

    // 计算属性
    const accounts = computed(() => store.getters.activeAccounts)
    const expenseMajorCategories = computed(() => store.state.categories.expenseMajor)
    const expenseMinorCategories = computed(() => store.state.categories.expenseMinor)
    const incomeMajorCategories = computed(() => store.state.categories.incomeMajor)
    const incomeMinorCategories = computed(() => store.state.categories.incomeMinor)

    const showSecondaryAccount = computed(() => {
      return ['转账', '借入', '借出', '收款', '还款'].includes(form.transaction_type)
    })

    // 方法
    const resetForm = () => {
      Object.assign(form, {
        transaction_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        transaction_type: '支出',
        amount: null,
        primary_account_id: null,
        secondary_account_id: null,
        expense_major_category_id: null,
        expense_minor_category_id: null,
        income_major_category_id: null,
        income_minor_category_id: null,
        counterparty_name: '',
        description: '',
        tags: ''
      })
    }

    const loadFormData = async () => {
      if (props.transaction) {
        Object.assign(form, {
          ...props.transaction,
          transaction_date: dayjs(props.transaction.transaction_date).format('YYYY-MM-DD HH:mm:ss')
        })

        if (form.expense_major_category_id) {
          await store.dispatch('fetchExpenseMinorCategories', form.expense_major_category_id)
        }
        if (form.income_major_category_id) {
          await store.dispatch('fetchIncomeMinorCategories', form.income_major_category_id)
        }
      } else {
        resetForm()
      }
    }

    const handleTypeChange = () => {
      // 清空分类选择
      form.expense_major_category_id = null
      form.expense_minor_category_id = null
      form.income_major_category_id = null
      form.income_minor_category_id = null
      form.secondary_account_id = null
      form.counterparty_name = ''
    }

    const handleExpenseMajorChange = () => {
      form.expense_minor_category_id = null
      if (form.expense_major_category_id) {
        store.dispatch('fetchExpenseMinorCategories', form.expense_major_category_id)
      }
    }

    const handleIncomeMajorChange = () => {
      form.income_minor_category_id = null
      if (form.income_major_category_id) {
        store.dispatch('fetchIncomeMinorCategories', form.income_major_category_id)
      }
    }

    const handleSave = async () => {
      try {
        await formRef.value.validate()
        loading.value = true
        
        const data = { ...form }
        
        if (props.isEdit) {
          await api.updateTransaction(props.transaction.id, data)
          ElMessage.success('更新成功')
        } else {
          await api.createTransaction(data)
          ElMessage.success('创建成功')
        }
        
        emit('saved')
      } catch (error) {
        console.error('保存失败:', error)
      } finally {
        loading.value = false
      }
    }

    const handleClosed = () => {
      formRef.value?.resetFields()
    }

    // 监听器
    watch(() => props.modelValue, async (newVal) => {
      if (newVal) {
        await loadFormData()
      }
    })

    // 生命周期
    onMounted(() => {
      store.dispatch('fetchExpenseMajorCategories')
      store.dispatch('fetchIncomeMajorCategories')
    })

    return {
      formRef,
      loading,
      form,
      rules,
      accounts,
      expenseMajorCategories,
      expenseMinorCategories,
      incomeMajorCategories,
      incomeMinorCategories,
      showSecondaryAccount,
      handleTypeChange,
      handleExpenseMajorChange,
      handleIncomeMajorChange,
      handleSave,
      handleClosed
    }
  }
}
</script>

<style scoped>
.el-select {
  width: 100%;
}
</style> 
